import cookie from 'js-cookie'
import { useSyncExternalStore } from 'react'

/**
 * Marque grise PharmUPP : le bandeau de navigation UPP ne s'affiche QUE pour une session
 * ouverte par le SSO PharmUPP.
 *
 * Le JWT ne porte pas la méthode d'authentification, et un refresh le réémettrait sans elle :
 * c'est donc le front qui pose ce marqueur au `signIn('ssoGroupPharmUpp')`, avec la durée du
 * cookie de refresh (30 jours), et l'efface à toute autre connexion, à la bascule de compte et
 * à la déconnexion (cf. AuthClient). Un switch-user admin ne le pose pas : l'admin ne s'est pas
 * connecté par UPP.
 *
 * Le marqueur est purement cosmétique (afficher un bandeau de liens publics) : il n'ouvre
 * aucun droit, d'où un cookie lisible côté client.
 */
const SSO_PHARMUPP_COOKIE = 'pharmedigroup-ssoPharmUpp'

// Hauteur visible du bandeau PharmUPP, tenue à jour par le bandeau lui-même au scroll et
// lue par la sidebar (Drawer fixe) pour démarrer sous lui. Absente = 0.
export const PHARMUPP_BANNER_OFFSET_VAR = '--pharmupp-banner-offset'
const SESSION_REFRESH_TTL_DAYS = 30

// Forçage du bandeau depuis le menu dev du header (NODE_ENV=development uniquement) : l'équipe
// n'a pas de compte SSO PharmUPP pour tester.
const DEV_FORCE_STORAGE_KEY = 'pharmedigroup-dev-force-pharmupp-banner'
const DEV_FORCE_CHANGE_EVENT = 'pharmedigroup-dev-force-pharmupp-banner'

export const markPharmUppSsoSession = (isPharmUppSso: boolean): void => {
  if (isPharmUppSso) {
    cookie.set(SSO_PHARMUPP_COOKIE, '1', { expires: SESSION_REFRESH_TTL_DAYS })
  } else {
    cookie.remove(SSO_PHARMUPP_COOKIE)
  }
}

export const isPharmUppSsoSession = (): boolean => cookie.get(SSO_PHARMUPP_COOKIE) === '1'

const subscribeDevForce = (callback: () => void) => {
  window.addEventListener('storage', callback)
  window.addEventListener(DEV_FORCE_CHANGE_EVENT, callback)

  return () => {
    window.removeEventListener('storage', callback)
    window.removeEventListener(DEV_FORCE_CHANGE_EVENT, callback)
  }
}

const readDevForce = (): boolean => {
  if (process.env.NODE_ENV !== 'development') return false
  try {
    return window.localStorage.getItem(DEV_FORCE_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export const setDevForcePharmUppBanner = (force: boolean): void => {
  if (process.env.NODE_ENV !== 'development') return
  try {
    if (force) {
      window.localStorage.setItem(DEV_FORCE_STORAGE_KEY, '1')
    } else {
      window.localStorage.removeItem(DEV_FORCE_STORAGE_KEY)
    }
  } catch {
    // localStorage indisponible : tant pis (dev only).
  }
  window.dispatchEvent(new Event(DEV_FORCE_CHANGE_EVENT))
}

export const useDevForcePharmUppBanner = (): boolean =>
  useSyncExternalStore(subscribeDevForce, readDevForce, () => false)

const readShowBanner = (): boolean => readDevForce() || isPharmUppSsoSession()

/**
 * Le bandeau PharmUPP doit-il être monté ? Session SSO PharmUPP, ou forçage dev.
 * Le cookie est relu à chaque rendu (il ne change qu'au login/logout) ; `false` côté serveur
 * pour ne pas désynchroniser l'hydratation.
 */
export const useShowPharmUppBanner = (): boolean =>
  useSyncExternalStore(subscribeDevForce, readShowBanner, () => false)
